import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import PageHeader from '../components/common/PageHeader';
import PartnerSelector from '../components/common/PartnerSelector';
import StatusChip from '../components/common/StatusChip';
import LoadingScreen from '../components/common/LoadingScreen';
import { useAuth } from '../context/AuthContext';
import { partnerService } from '../services/partnerService';
import { formatDate, formatDeviceType, formatMs, getErrorMessage } from '../utils/format';

export default function UsageLogs() {
  const { user, isAdmin } = useAuth();
  const [partners, setPartners] = useState([]);
  const [partnerId, setPartnerId] = useState('');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function init() {
      setLoading(true);
      try {
        if (isAdmin) {
          const res = await partnerService.list({ limit: 100 });
          const list = res.partners || [];
          setPartners(list);
          if (list[0]) setPartnerId(list[0].id);
        } else if (user?.partnerId) {
          setPartnerId(user.partnerId);
        }
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    }
    init();
  }, [isAdmin, user?.partnerId]);

  useEffect(() => {
    if (!partnerId) return;
    partnerService
      .getUsage(partnerId, { days: 30, limit: 200 })
      .then(usage => setLogs(usage.recent || []))
      .catch(err => setError(getErrorMessage(err)));
  }, [partnerId]);

  if (loading) return <LoadingScreen />;

  return (
    <>
      <PageHeader
        title="Usage Logs"
        subtitle="Recent parser API calls and response metrics"
        action={
          isAdmin ? (
            <PartnerSelector partners={partners} value={partnerId} onChange={setPartnerId} />
          ) : null
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        <TableContainer sx={{ maxHeight: 640 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Endpoint</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Response</TableCell>
                <TableCell>API Key</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography color="text.secondary" align="center" py={4}>
                      No usage logs found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {logs.map(log => (
                <TableRow key={log._id || log.id} hover>
                  <TableCell>{formatDate(log.createdAt)}</TableCell>
                  <TableCell>{formatDeviceType(log.deviceType)}</TableCell>
                  <TableCell>{log.endpoint}</TableCell>
                  <TableCell>
                    <StatusChip status={log.success ? 'active' : 'past_due'} label={log.success ? 'Success' : 'Failed'} />
                  </TableCell>
                  <TableCell>{formatMs(log.responseTimeMs)}</TableCell>
                  <TableCell>{log.apiKeyId?.keyPrefix || '—'}…</TableCell>
                  <TableCell>
                    <Box sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {log.errorMessage || '—'}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </>
  );
}
